import { doc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import {
  defaultSwissRounds,
  generateDoubleElim,
  generateGroupsPlayoff,
  generateRoundRobin,
  generateSingleElim,
  generateSwissRound1,
} from '@/engine';
import type { EngineParticipant, GeneratedMatch } from '@/engine/types';
import type { Match, Participant, Tournament } from './types';

function buildMatchDoc(
  gen: GeneratedMatch,
  participantById: Map<string, Participant>
): Match {
  const aName = gen.playerAId ? participantById.get(gen.playerAId)?.displayName ?? null : null;
  const bName = gen.playerBId ? participantById.get(gen.playerBId)?.displayName ?? null : null;
  let status: Match['status'] = 'PENDING';
  if (gen.autoAdvanceWinnerId) {
    status = 'COMPLETED';
  } else if (gen.playerAId && gen.playerBId) {
    status = 'READY';
  }
  const m: Match = {
    id: gen.id,
    stage: gen.stage,
    round: gen.round,
    slot: gen.slot,
    playerAId: gen.playerAId,
    playerBId: gen.playerBId,
    playerAName: aName,
    playerBName: bName,
    status,
    ...(gen.nextMatchId ? { nextMatchId: gen.nextMatchId } : {}),
    ...(gen.nextMatchSlot ? { nextMatchSlot: gen.nextMatchSlot } : {}),
    ...(gen.loserNextMatchId ? { loserNextMatchId: gen.loserNextMatchId } : {}),
    ...(gen.loserNextMatchSlot ? { loserNextMatchSlot: gen.loserNextMatchSlot } : {}),
    ...(gen.deadSlot ? { deadSlot: gen.deadSlot } : {}),
    ...(gen.groupId ? { groupId: gen.groupId } : {}),
  };
  if (gen.autoAdvanceWinnerId) {
    m.score = {
      sets: [],
      winnerId: gen.autoAdvanceWinnerId,
      summary: 'walkover',
    };
  }
  return m;
}

export async function startTournament(
  tournament: Tournament,
  participants: Participant[]
): Promise<void> {
  if (tournament.status !== 'DRAFT' && tournament.status !== 'READY') {
    throw new Error('Турнир уже стартовал или завершён');
  }
  if (participants.length < 2) {
    throw new Error('Нужно минимум 2 участника');
  }
  // Сортируем участников по seed; те без seed получают конец списка.
  const sorted = [...participants].sort((a, b) => {
    const sa = a.seed ?? Number.POSITIVE_INFINITY;
    const sb = b.seed ?? Number.POSITIVE_INFINITY;
    return sa - sb;
  });

  // Если режим RANDOM — перемешать всех и переприсвоить seed по позициям.
  let inputs: EngineParticipant[];
  if (tournament.seedingMode === 'RANDOM') {
    const shuffled = [...sorted];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    inputs = shuffled.map((p, idx) => ({ id: p.id, seed: idx + 1 }));
  } else {
    inputs = sorted.map((p, idx) => ({ id: p.id, seed: p.seed ?? idx + 1 }));
  }

  let engine;
  if (tournament.format === 'SINGLE_ELIM') {
    engine = generateSingleElim(inputs);
  } else if (tournament.format === 'ROUND_ROBIN') {
    engine = generateRoundRobin(inputs);
  } else if (tournament.format === 'SWISS') {
    engine = generateSwissRound1(inputs);
  } else if (tournament.format === 'DOUBLE_ELIM') {
    engine = generateDoubleElim(inputs);
  } else if (tournament.format === 'GROUPS_PLAYOFF') {
    if (!tournament.groupConfig) throw new Error('groupConfig не задан');
    engine = generateGroupsPlayoff(inputs, tournament.groupConfig);
  } else {
    throw new Error(`Формат ${tournament.format} ещё не реализован`);
  }
  const participantById = new Map(participants.map((p) => [p.id, p]));

  // Движок (план 04) уже возвращает сетку с продвинутыми bye-победителями:
  // в R2-матчах playerAId/playerBId, питаемые bye-матчами, уже заполнены.
  // buildMatchDoc подтянет playerAName/playerBName по этим id автоматически.
  const initialMatches = engine.matches.map((g) => buildMatchDoc(g, participantById));

  // Остаётся только пересчитать статус: R2-матч, у которого оба слота уже
  // заполнены bye-победителями, готов к игре.
  for (const m of initialMatches) {
    if (m.status === 'PENDING' && m.playerAId && m.playerBId) {
      m.status = 'READY';
    }
  }

  const batch = writeBatch(db);
  for (const m of initialMatches) {
    const ref = doc(db, 'tournaments', tournament.id, 'matches', m.id);
    batch.set(ref, m);
  }
  batch.update(doc(db, 'tournaments', tournament.id), {
    status: 'IN_PROGRESS',
    ...(tournament.format === 'SWISS' && !tournament.swissRounds
      ? { swissRounds: defaultSwissRounds(participants.length) }
      : {}),
    ...(engine.groups ? { groups: engine.groups } : {}),
    updatedAt: serverTimestamp(),
  });
  await batch.commit();
}
